<template>
    <div class="order-edit__form">
        <template v-if="schema.details">
            <form @submit.prevent="updateOrder">
                <Grid>
                    <Column v-if="schema.customer" :span="6">
                        <FormField name="customer" :error="error" label="Customers">
                            <CustomSelect
                                v-model="orderData.customer"
                                emit-prop="id"
                                view-label="name"
                                :options="customers"
                                required
                            />
                        </FormField>
                    </Column>
                    <Column v-if="schema.eta" :span="6">
                        <FormField name="eta" :error="error" label="ETA">
                            <DateInput v-model="orderData.eta" />
                        </FormField>
                    </Column>
                </Grid>
                <Grid>
                    <Column :span="6">
                        <FormField name="name" :error="error" label="Book Title" required>
                            <FormInput v-model="orderData.details.name" />
                        </FormField>
                    </Column>
                    <Column :span="6">
                        <FormField name="isbnx" :error="error" label="ISBN">
                            <FormInput v-model="orderData.details.isbnx" />
                        </FormField>
                    </Column>
                </Grid>
                <Grid>
                    <Column :span="12">
                        <FormField name="raw_authors" :error="error" label="Authors">
                            <AuthorsConstructor v-model="orderData.details.raw_authors" />
                        </FormField>
                    </Column>
                    <Column :span="12">
                        <FormField name="comment" :error="error" label="Description">
                            <textarea
                                v-model="orderData.details.comment"
                                class="form__textarea form__textarea--fullwidth"
                                rows="5"
                                placeholder="Book description"
                            ></textarea>
                        </FormField>
                    </Column>
                </Grid>
                <Grid>
                    <Column :span="8">
                        <FormField name="book_language" :error="error" label="Book Language">
                            <CustomSelect
                                v-model="orderData.details.book_language"
                                emit-prop="id"
                                view-label="name"
                                :options="arrayBookLanguages"
                                required
                            />
                        </FormField>
                    </Column>
                    <Column :span="3">
                        <FormField name="speed" :error="error" label="Speed">
                            <NumberInput
                                v-model="orderData.details.speed"
                                :step="0.05"
                                :min="0.5"
                                :max="3"
                            />
                        </FormField>
                    </Column>
                </Grid>
                <Grid>
                    <Column :span="8">
                        <FormField name="pause_config" :error="error" label="Pause Config">
                            <CustomSelect
                                v-model="orderData.details.pause_config"
                                emit-prop="id"
                                view-label="label"
                                :options="arrayPauseConfigs"
                                clearable
                            />
                        </FormField>
                    </Column>
                    <Column :span="3">
                        <FormField name="volume" :error="error" label="Volume">
                            <NumberInput
                                v-model="orderData.details.volume"
                                :step="0.1"
                                :min="0.5"
                                :max="3"
                            />
                        </FormField>
                    </Column>
                </Grid>
                <Grid>
                    <Column :span="12">
                        <FormField name="speaker" :error="error" label="Speaker" required>
                            <SpeakerSelect
                                v-model="orderData.details.speaker"
                                :speakers="speakers"
                                :empty-text="'Please select book language'"
                            />
                        </FormField>
                    </Column>
                </Grid>
                <Grid v-if="schema.document">
                    <Column :span="12">
                        <FormField name="document" :error="error" label="File">
                            <div v-if="!changeFile">
                                File Uploaded: {{ documentBasename }}
                                <button type="button" class="button" @click="changeFile = true">
                                    Change File
                                </button>
                            </div>
                            <FileInput
                                v-if="changeFile"
                                style="width: 100%"
                                accept=".docx, .epub, .fb2, .rtf, .pdf"
                                label="Or upload the book in .docx or .epub, .fb2, .rtf"
                                with-link
                                :required="true"
                                @selected="setFile"
                                @update:link="orderData.document = $event"
                            />
                        </FormField>
                    </Column>
                </Grid>
                <Grid>
                    <Column :span="6">
                        <button
                            type="button"
                            class="button button--medium button--fullwidth button--outlined"
                            @click.prevent="cancel"
                        >
                            Cancel
                        </button>
                    </Column>
                    <Column :span="6">
                        <button :disabled="loading" class="button button--medium button--fullwidth">
                            Save
                        </button>
                    </Column>
                </Grid>
            </form>
        </template>
    </div>
</template>

<script>
import {
    FormField,
    FormInput,
    CustomSelect,
    NumberInput,
    DateInput,
    FileInput,
} from '@speechki-frontend/shared/ui/forms';
import SpeakerSelect from '@speechki-frontend/shared/ui/SpeakerSelect';
import { Grid, Column } from '@speechki-frontend/shared/ui/Grid';
import AuthorsConstructor from '../AuthorsConstructor/AuthorsConstructor';

import { fetchBookLanguages, fetchPauseConfigs } from '@speechki-frontend/core/book/api/books';
import { fetchSpeakers } from '@speechki-frontend/core/editor/api/speakers';

import { mapActions, mapState } from 'vuex';

import { removeEmptyFields } from '@speechki-frontend/shared/helpers/functions';

import { updateOrder } from '../../api/orders';

import { format } from 'date-fns';

export default {
    name: 'OrderEditForm',
    components: {
        FormField,
        FormInput,
        CustomSelect,
        NumberInput,
        SpeakerSelect,
        DateInput,
        FileInput,
        Grid,
        Column,
        AuthorsConstructor,
    },

    props: {
        schema: {
            type: Object,
            required: true,
        },
        modelValue: {
            type: Object,
            required: true,
        },
    },

    emits: ['update:modelValue', 'cancel', 'save'],

    data() {
        return {
            orderData: {},
            error: undefined,
            loading: false,
            bookLanguages: [],
            speakers: [],
            pauseConfigs: [],
            changeFile: false,
        };
    },

    computed: {
        ...mapState('order', {
            customers: (state) => state.customers,
        }),
        arrayBookLanguages() {
            return Object.keys(this.bookLanguages).map((lang) => {
                return {
                    id: lang,
                    name: this.bookLanguages[lang],
                };
            });
        },
        arrayPauseConfigs() {
            const { locale } = this.$i18n;
            return this.pauseConfigs.map(({ id, name }) => ({ id, label: name[locale] }));
        },
        documentBasename() {
            const { document } = this.modelValue;
            return document.split('/').pop().split('?').shift();
        },
    },

    watch: {
        async 'modelValue.details.book_language'(value) {
            this.orderData.details.speaker = undefined;

            if (!value) return;

            this.fetchSpeakers();
        },
    },

    async created() {
        this.loading = true;
        this.prepareSchemaFields();

        try {
            await Promise.all([
                this.fetchBookLanguages(),
                this.fetchCustomers(),
                this.fetchSpeakers(),
                this.fetchPauseConfigs(),
            ]);
        } catch (e) {
            console.error(e);
        } finally {
            this.loading = false;
        }
    },

    methods: {
        ...mapActions('order', ['fetchCustomers']),
        prepareSchemaFields() {
            Object.keys(this.schema).forEach((field) => {
                if (field === 'eta') {
                    const value = this.modelValue.eta ? new Date(this.modelValue.eta) : '';
                    this.orderData.eta = value;
                }

                if (field === 'details') {
                    const details = {
                        name: this.modelValue.details.name || '',
                        isbnx: this.modelValue.details.isbnx || '',
                        comment: this.modelValue.details.comment || '',
                        book_language: this.modelValue.details.book_language,
                        speed: this.modelValue.details.speed,
                        volume: this.modelValue.details.volume,
                        speaker: this.modelValue.speaker.id || undefined,
                        raw_authors: this.modelValue.details.raw_authors,
                        pause_config:
                            parseInt((this.modelValue.details.pause_config || {}).id) || 0,
                    };

                    this.orderData.details = details;
                }

                if (field === 'document') {
                    this.orderData.document = undefined;
                }

                if (field === 'customer') {
                    this.orderData.customer = this.modelValue.customer.id;
                }
            });
        },
        async fetchBookLanguages() {
            this.bookLanguages = await fetchBookLanguages();
        },

        async fetchSpeakers() {
            this.speakers = await fetchSpeakers({
                book_language: this.modelValue.details.book_language,
            });
        },
        async fetchPauseConfigs() {
            this.pauseConfigs = await fetchPauseConfigs();
        },
        setFile(file) {
            this.orderData.document = file;
        },
        cancel() {
            this.$emit('cancel');
        },
        async updateOrder() {
            let data = new FormData();

            const order = this.orderData;

            if (order.document && typeof order.document === 'string') {
                data.set('url', order.document);
            }

            if (order.document && typeof order.document === 'object') {
                data.set('document', order.document);
            }

            const details = removeEmptyFields(order.details);
            details.raw_authors = details.raw_authors
                .slice()
                .filter((author) => {
                    if (!author.last_name && !author.first_name && !author.patronymic) {
                        return false;
                    }

                    return true;
                })
                .map(removeEmptyFields);

            const pauseConfigId = parseInt(details.pause_config);
            details.pause_config = this.arrayPauseConfigs.find(({ id }) => id == pauseConfigId);

            data.set('details', JSON.stringify({ ...details }));

            if (order.eta) {
                data.set('eta', format(new Date(order.eta), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"));
            }

            if (order.customer) {
                data.set('customer', order.customer);
            }

            this.loading = true;

            try {
                await updateOrder(this.modelValue.id, data);
                this.$emit('save');
            } catch (e) {
                this.error = e;
                console.log(e);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
